import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Check, Plus, Workflow } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	type CustomBot,
	createScenario,
	listScenarios,
	updateBot,
} from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { Skeleton } from "@/shared/ui/skeleton"
import { LoadSwap } from "@/shared/ui/load-swap"

/** Имя нового сценария по умолчанию: по нему сценарий узнают в списке. */
export function defaultScenarioName(bot: Pick<CustomBot, "name" | "username">): string {
	return `Сценарий ${bot.name || `@${bot.username}`}`
}

/**
 * Сценарий бота: воронки, меню, группы. Обычно у бота свой сценарий, и он создаётся
 * здесь же. Подключить чужой нужно, когда два бота должны вести себя одинаково.
 */
export function ScenarioPicker({ bot, onDone }: { bot: CustomBot; onDone?: () => void }) {
	const queryClient = useQueryClient()
	const [name, setName] = useState(defaultScenarioName(bot))
	const [selected, setSelected] = useState<string | null>(null)
	const scenarios = useQuery({ queryKey: botBuilderKeys.scenarios, queryFn: listScenarios })
	const others = (scenarios.data ?? []).filter((scenario) => scenario.id !== bot.scenarioId)

	const done = async (message: string) => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: botBuilderKeys.bot(bot.id) }),
			queryClient.invalidateQueries({ queryKey: botBuilderKeys.bots }),
			queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios }),
		])
		toast.success(message)
		onDone?.()
	}

	const create = useMutation({
		mutationFn: async () => {
			const scenario = await createScenario({ name: name.trim() })
			await updateBot(bot.id, { scenarioId: scenario.id })
		},
		onSuccess: () => done("Сценарий создан и подключён"),
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось создать сценарий")),
	})
	const attach = useMutation({
		mutationFn: (scenarioId: string) => updateBot(bot.id, { scenarioId }),
		onSuccess: () => done("Сценарий подключён"),
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось подключить сценарий")),
	})

	return (
		<div className="space-y-6">
			<form
				className="rounded-2xl border border-primary/40 bg-primary/5 p-4"
				onSubmit={(event) => {
					event.preventDefault()
					if (name.trim()) create.mutate()
				}}
			>
				<p className="font-medium">Новый сценарий для этого бота</p>
				<p className="mt-1 text-sm text-muted-foreground">
					Пустой сценарий: дальше соберёте воронки и меню в конструкторе.
				</p>
				<div className="mt-3 flex flex-col gap-2 sm:flex-row">
					<Input
						aria-label="Название сценария"
						placeholder="Например, Основной сценарий"
						value={name}
						onChange={(event) => setName(event.target.value)}
						maxLength={120}
					/>
					<Button type="submit" disabled={!name.trim()} isLoading={create.isPending} className="shrink-0">
						<Plus />
						Создать
					</Button>
				</div>
			</form>

			<LoadSwap loading={scenarios.isLoading}>
				{scenarios.isLoading ? (
				<Skeleton className="h-24 rounded-2xl" />
			) : others.length ? (
				<div>
					<p className="mb-2 text-sm font-medium">Или подключите готовый</p>
					<ul className="space-y-1.5" role="radiogroup" aria-label="Готовые сценарии">
						{others.map((scenario) => {
							const active = selected === scenario.id
							return (
								<li key={scenario.id}>
									<button
										type="button"
										role="radio"
										aria-checked={active}
										onClick={() => setSelected(scenario.id)}
										className={cn(
											"flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors",
											active ? "border-primary/60 bg-primary/5" : "border-border hover:bg-accent/40",
										)}
									>
										<span className="grid size-9 shrink-0 place-items-center rounded-lg bg-secondary">
											<Workflow className="size-4" />
										</span>
										<span className="min-w-0 flex-1">
											<span className="block truncate text-sm font-medium">{scenario.name}</span>
											<span className="block truncate text-xs text-muted-foreground">
												{scenario.nodeCount} блоков ·{" "}
												{scenario.latestVersion ? `версия ${scenario.latestVersion.number}` : "не опубликован"}
												{scenario.bots.length
													? ` · уже у ${scenario.bots.map((item) => `@${item.username}`).join(", ")}`
													: ""}
											</span>
										</span>
										{active ? <Check className="size-4 text-primary" /> : null}
									</button>
								</li>
							)
						})}
					</ul>
					<Button
						variant="secondary"
						className="mt-3"
						disabled={!selected}
						isLoading={attach.isPending}
						onClick={() => selected && attach.mutate(selected)}
					>
						Подключить выбранный
					</Button>
					<p className="mt-2 text-xs text-muted-foreground">
						Общий сценарий меняется сразу у всех ботов, которые его используют.
					</p>
				</div>
			) : null}
			</LoadSwap>
		</div>
	)
}
